// Studio Admin shell — bootstraps auth via the bridge handshake, then
// renders the navigator + active view. Uses route segments under the page
// basename (/pages/studio-admin), so individual pages map to:
//   /pages/studio-admin             → Templates (default)
//   /pages/studio-admin/customers   → Customer browser
//   /pages/studio-admin/customer/:id → Customer detail
//   /pages/studio-admin/audit       → Activity log
//   /pages/studio-admin/team        → Admin management (owners only)

import { useEffect, useState } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LayoutGrid, Users, Activity, Shield, AlertCircle } from 'lucide-react';
import { bridgeAuthenticate, getMe, ApiError } from './lib/api';
import { TemplatesView } from './views/Templates';
import { CustomersView } from './views/Customers';
import { CustomerDetailView } from './views/CustomerDetail';
import { AuditView } from './views/Audit';
import { TeamView } from './views/Team';

export function App() {
  const [bootState, setBootState] = useState<'pending' | 'ready' | 'error'>('pending');
  const [bootError, setBootError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        await bridgeAuthenticate();
        setBootState('ready');
      } catch (err) {
        setBootError(err instanceof ApiError ? err.message : (err as Error).message);
        setBootState('error');
      }
    })();
  }, []);

  if (bootState === 'pending') {
    return <div className="boot">Loading…</div>;
  }
  if (bootState === 'error') {
    return (
      <div className="boot boot--error">
        <AlertCircle size={32} />
        <h2>Couldn't sign you in</h2>
        <p>{bootError ?? 'Bridge handshake failed.'}</p>
        <p>Hard-reload the page. If this persists, ping engineering.</p>
      </div>
    );
  }

  return <AuthedShell />;
}

function AuthedShell() {
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: getMe,
  });

  if (meQuery.isLoading) return <div className="boot">Loading admin profile…</div>;
  if (meQuery.isError) {
    const err = meQuery.error as ApiError;
    if (err.status === 403) {
      return (
        <div className="boot boot--error">
          <AlertCircle size={32} />
          <h2>Not an admin</h2>
          <p>Your email isn't on the studio_admins allowlist.</p>
        </div>
      );
    }
    return <div className="boot boot--error">Failed to load profile: {err.message}</div>;
  }
  const me = meQuery.data!;

  return (
    <div className="admin-shell">
      <aside className="sidebar">
        <div className="sidebar__brand">
          <strong>MD Studio Admin</strong>
          <span className="sidebar__shop">{me.session_shop_domain}</span>
        </div>
        <nav className="sidebar__nav">
          <NavLink to="/" end className={({ isActive }) => `nav-item${isActive ? ' is-active' : ''}`}>
            <LayoutGrid size={16} /> <span>Templates</span>
          </NavLink>
          <NavLink to="/customers" className={({ isActive }) => `nav-item${isActive ? ' is-active' : ''}`}>
            <Users size={16} /> <span>Customers</span>
          </NavLink>
          <NavLink to="/audit" className={({ isActive }) => `nav-item${isActive ? ' is-active' : ''}`}>
            <Activity size={16} /> <span>Activity</span>
          </NavLink>
          {me.admin.role === 'owner' && (
            <NavLink to="/team" className={({ isActive }) => `nav-item${isActive ? ' is-active' : ''}`}>
              <Shield size={16} /> <span>Team</span>
            </NavLink>
          )}
        </nav>
        <div className="sidebar__user">
          <div className="sidebar__user-email">{me.admin.email}</div>
          <div className="sidebar__user-role">{me.admin.role}</div>
        </div>
      </aside>

      <main className="main">
        <Routes>
          <Route path="/" element={<TemplatesView shopDomain={me.session_shop_domain} role={me.admin.role} />} />
          <Route path="/customers" element={<CustomersView shopDomain={me.session_shop_domain} />} />
          <Route path="/customer/:id" element={<CustomerDetailView />} />
          <Route path="/audit" element={<AuditView />} />
          {me.admin.role === 'owner' && <Route path="/team" element={<TeamView />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
